import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Sparkles, Sliders, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (n) => n.toLocaleString();

export const CustomBoostSlider = ({ onBuy, platform = "instagram" }) => {
  const [followers, setFollowers] = useState(2500);
  const [quote, setQuote] = useState({ amount: 19.99, followers: 2500 });
  const [loading, setLoading] = useState(false);

  const isTikTok = platform === "tiktok";

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(
          `/pricing/quote?followers=${followers}&platform=${platform}`
        );
        setQuote(r.data);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [followers, platform]);

  const buy = () => {
    onBuy({
      id: "pkg_custom",
      followers: quote.followers,
      amount: quote.amount,
      label: `${fmt(quote.followers)} Followers`,
      isCustom: true,
      platform,
    });
  };

  const pricePerK = (quote.amount / quote.followers) * 1000;

  const MIN = 500;
  const MAX = 50000;
  const pct = (val) => ((val - MIN) / (MAX - MIN)) * 100;
  const ticks = [500, 10000, 25000, 50000];
  const tickLabel = (v) =>
    v >= 1000 ? `${Math.round(v / 1000)}K` : `${v}`;

  const accent = isTikTok ? "#00F2EA" : "#FF007F";
  const gradientClass = isTikTok ? "text-gradient-tiktok" : "text-gradient-neon";
  const bgClass = isTikTok ? "bg-gradient-tiktok" : "bg-neon";
  const glowClass = isTikTok ? "glow-tiktok" : "glow-pink";

  return (
    <section id="custom" className="py-28 relative border-t border-white/10">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <div
            className="uppercase text-xs font-bold tracking-[0.2em] mb-4 inline-flex items-center gap-2"
            style={{ color: isTikTok ? "#00F2EA" : "#FF5E00" }}
          >
            <Sliders className="w-3 h-3" /> Build your own
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
            Need something <span className={gradientClass}>in between?</span>
          </h2>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">
            Drag the slider to pick exactly how many {isTikTok ? "TikTok" : "Instagram"} followers you want, 500 to 50,000.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.25 }}
          className="bg-[#0A0A0A] border-2 border-white/10 rounded-3xl p-8 md:p-12"
        >
          {/* Headline numbers */}
          <div className="grid sm:grid-cols-2 gap-6 mb-10 items-end">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/40 mb-2">
                Followers
              </div>
              <div
                data-testid="custom-followers-display"
                className="font-display text-5xl md:text-6xl font-black tracking-tighter text-white"
              >
                {fmt(quote.followers)}
              </div>
            </div>
            <div className="sm:text-right">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/40 mb-2">
                Total
              </div>
              <div
                data-testid="custom-price-display"
                className={`font-display text-5xl md:text-6xl font-black tracking-tighter ${gradientClass} inline-flex items-baseline gap-2`}
              >
                {loading ? (
                  <Loader2
                    className="w-6 h-6 animate-spin mr-2"
                    style={{ color: accent }}
                  />
                ) : null}
                ${quote.amount.toFixed(2)}
              </div>
              <div className="text-xs text-white/40 mt-1">
                ≈ ${pricePerK.toFixed(2)} per 1K
              </div>
            </div>
          </div>

          {/* Slider */}
          <div className="mb-4">
            <div className="relative">
              {/* Floating thumb label */}
              <div
                className="absolute -top-9 pointer-events-none transition-[left] duration-75"
                style={{
                  left: `${pct(followers)}%`,
                  transform: "translateX(-50%)",
                }}
              >
                <div
                  data-testid="custom-thumb-label"
                  className="text-white text-xs font-black px-2.5 py-1 rounded-md whitespace-nowrap"
                  style={{
                    background: accent,
                    boxShadow: `0 0 18px ${isTikTok ? "rgba(0,242,234,0.55)" : "rgba(255,0,127,0.55)"}`,
                  }}
                >
                  {fmt(followers)}
                </div>
                <div
                  className="w-2 h-2 rotate-45 mx-auto -mt-1"
                  style={{ background: accent }}
                />
              </div>

              <Slider
                data-testid="custom-followers-slider"
                value={[followers]}
                onValueChange={([v]) => setFollowers(v)}
                min={MIN}
                max={MAX}
                step={100}
                className={
                  isTikTok
                    ? "[&_[role=slider]]:bg-gradient-tiktok [&_[role=slider]]:border-0 [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:shadow-[0_0_24px_rgba(0,242,234,0.6)]"
                    : "[&_[role=slider]]:bg-neon [&_[role=slider]]:border-0 [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:shadow-[0_0_24px_rgba(255,0,127,0.6)]"
                }
              />
            </div>

            {/* Tick labels — positioned to actual slider value */}
            <div className="relative h-5 mt-3">
              {ticks.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setFollowers(v)}
                  className="absolute -translate-x-1/2 text-[10px] uppercase tracking-[0.2em] font-bold text-white/40 hover:text-white transition"
                  style={{ left: `${pct(v)}%` }}
                >
                  {tickLabel(v)}
                </button>
              ))}
            </div>
          </div>

          {/* Quick picks */}
          <div className="flex flex-wrap gap-2 mt-6 mb-8">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/40 self-center mr-2">
              Quick pick:
            </span>
            {[1000, 3000, 7500, 15000, 30000].map((v) => (
              <button
                key={v}
                onClick={() => setFollowers(v)}
                data-testid={`quick-pick-${v}`}
                className={`text-xs font-bold px-3 py-1.5 rounded-full border transition ${
                  followers === v
                    ? isTikTok
                      ? "bg-[#00F2EA]/10 border-[#00F2EA]/40 text-[#00F2EA]"
                      : "bg-[#FF007F]/10 border-[#FF007F]/40 text-[#FF007F]"
                    : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                }`}
              >
                {fmt(v)}
              </button>
            ))}
          </div>

          <button
            data-testid="custom-buy-btn"
            onClick={buy}
            disabled={loading}
            className={`w-full ${bgClass} text-white font-bold py-4 rounded-full hover:scale-[1.01] transition-transform ${glowClass} inline-flex items-center justify-center gap-2 disabled:opacity-60`}
          >
            <Sparkles className="w-4 h-4" />
            Get {fmt(quote.followers)} followers — ${quote.amount.toFixed(2)}
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default CustomBoostSlider;
